import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Settings, Store, Smartphone, Shield, CheckCircle2, Plus } from 'lucide-react'
import { guardianService, type GuardianSettings } from '@/services/guardian'

export function GuardianSettingsTab() {
  const [settings, setSettings] = useState<GuardianSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [buildSource, setBuildSource] = useState('glaium')

  useEffect(() => { loadSettings() }, [])

  async function loadSettings() {
    setLoading(true)
    try { setSettings(await guardianService.getSettings()) }
    catch (e) { console.error('Failed to load settings:', e) }
    finally { setLoading(false) }
  }

  async function handleChange(field: string, value: unknown) {
    try {
      await guardianService.updateSettings({ [field]: value })
      setSettings(prev => prev ? { ...prev, [field]: value } : null)
    } catch (e) { console.error('Failed to update:', e) }
  }

  if (loading) {
    return <div className="space-y-6"><Skeleton className="h-40 rounded-xl" /><div className="grid grid-cols-2 gap-6"><Skeleton className="h-64 rounded-xl" /><Skeleton className="h-64 rounded-xl" /></div></div>
  }

  return (
    <div className="space-y-6">

      {/* App Store Connections — full width, prominent */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-100 to-indigo-100 flex items-center justify-center">
                <Store className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <CardTitle className="text-base">App Store Connections</CardTitle>
                <CardDescription className="text-xs">Connect to app stores to automate build submission after dependency updates are merged.</CardDescription>
              </div>
            </div>
            <Button variant="outline" size="sm" className="gap-1.5 text-xs">
              <Plus className="h-3.5 w-3.5" />
              Connect Store
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Google Play */}
            <div className="rounded-xl border p-4 space-y-3">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <Smartphone className="h-5 w-5 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-slate-900">Google Play Store</p>
                    <Badge variant="secondary" className="bg-emerald-50 text-emerald-600 border-0 text-[10px]">
                      <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" /> Connected
                    </Badge>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-0.5">Service Account: guardian@glaium-prod.iam.gserviceaccount.com</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="h-7 text-xs flex-1">Test Connection</Button>
                <Button variant="ghost" size="sm" className="h-7 text-xs text-red-500 hover:text-red-700 hover:bg-red-50">Revoke</Button>
              </div>
            </div>

            {/* Apple App Store */}
            <div className="rounded-xl border p-4 space-y-3">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center">
                  <svg className="h-5 w-5 text-slate-700" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-slate-900">App Store Connect</p>
                    <Badge variant="secondary" className="bg-emerald-50 text-emerald-600 border-0 text-[10px]">
                      <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" /> Connected
                    </Badge>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-0.5">API Key: A1B2C3D4E5 | Issuer: 69a6de7e-...</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="h-7 text-xs flex-1">Test Connection</Button>
                <Button variant="ghost" size="sm" className="h-7 text-xs text-red-500 hover:text-red-700 hover:bg-red-50">Revoke</Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bottom row: 2 columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Left: Scan & Merge Config */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Settings className="h-4 w-4 text-slate-400" />
              <CardTitle className="text-sm">Scan & Merge Defaults</CardTitle>
            </div>
            <CardDescription className="text-xs">Portfolio-wide defaults. Override per app in the Apps tab.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-0">
            <div className="flex items-center justify-between py-3">
              <div>
                <p className="text-sm font-medium text-slate-900">Scan frequency</p>
                <p className="text-xs text-slate-500">How often to check for updates</p>
              </div>
              <Select value={settings?.scan_frequency || 'daily'} onValueChange={v => handleChange('scan_frequency', v)}>
                <SelectTrigger className="w-28 h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Separator />
            <div className="flex items-center justify-between py-3">
              <div>
                <p className="text-sm font-medium text-slate-900">Auto-merge</p>
                <p className="text-xs text-slate-500">Merge low-risk PRs automatically</p>
              </div>
              <Switch
                checked={settings?.auto_merge_enabled || false}
                onCheckedChange={v => handleChange('auto_merge_enabled', v)}
              />
            </div>
            {settings?.auto_merge_enabled && (
              <>
                <Separator />
                <div className="flex items-center justify-between py-3 pl-4 border-l-2 border-slate-200">
                  <p className="text-xs text-slate-500">Merge policy</p>
                  <Select value={settings?.auto_merge_policy || 'patch_devdeps_only'} onValueChange={v => handleChange('auto_merge_policy', v)}>
                    <SelectTrigger className="w-44 h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="patch_devdeps_only">Patch + Dev deps only</SelectItem>
                      <SelectItem value="patch_all">All patch updates</SelectItem>
                      <SelectItem value="minor_devdeps">Minor + Dev deps</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Right: Build & Release Config */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-slate-400" />
              <CardTitle className="text-sm">Build & Release Defaults</CardTitle>
            </div>
            <CardDescription className="text-xs">Default build and submission configuration for all apps.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-0">
            <div className="py-3 space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-900">Build source</p>
                  <p className="text-xs text-slate-500">Who builds after PR merge</p>
                </div>
                <Select defaultValue="glaium" onValueChange={v => setBuildSource(v)}>
                  <SelectTrigger className="w-44 h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="glaium">Glaium builds</SelectItem>
                    <SelectItem value="external">Customer builds</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {buildSource === 'external' && (
                <div className="pl-4 border-l-2 border-slate-200 space-y-1.5">
                  <p className="text-xs text-slate-500">Build artifact path</p>
                  <input
                    type="text"
                    placeholder="gs://your-bucket/builds/{app}/{version}/"
                    className="w-full h-8 text-xs px-3 rounded-md border border-slate-200 bg-white text-slate-700 placeholder:text-slate-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-[10px] text-slate-400">Use {'{app}'} and {'{version}'} as placeholders.</p>
                </div>
              )}
            </div>
            <Separator />
            <div className="flex items-center justify-between py-3">
              <div>
                <p className="text-sm font-medium text-slate-900">Release notes</p>
                <p className="text-xs text-slate-500">"What's New" for store submissions</p>
              </div>
              <Select defaultValue="auto">
                <SelectTrigger className="w-44 h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto-generate (AI)</SelectItem>
                  <SelectItem value="template">Use template</SelectItem>
                  <SelectItem value="manual">Manual each time</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Separator />
            <div className="flex items-center justify-between py-3">
              <div>
                <p className="text-sm font-medium text-slate-900">Production approval</p>
                <p className="text-xs text-slate-500">Require approval before release</p>
              </div>
              <Switch defaultChecked={true} />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
